"use client";

import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  Plus,
  X,
  Trash2,
  Edit,
  AlertTriangleIcon,
  DownloadCloudIcon,
  TrashIcon,
  UploadIcon,
  Save,
} from "lucide-react";
import BookingTable from "../common/Table";
import DynamicForm from "./Popup";
import Button from "./Button";
import { 
  useGetLocalPackages, 
  useAddLocalPackage, 
  useUpdateLocalPackage, 
  useUpdateMasterLocalPackageStatus, 
  useDeleteLocalPackages,
  useGetBookingTypesAndModes 
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { toast } from "react-toastify";

interface LocalPackage {
  local_package_id: number;
  booking_type: number;
  booking_mode: number;
  booking_mode_name: string;
  booking_type_name: string;
  package_name: string;
  hrs: number;
  km: number;
  status: number;
  display_order: number;
  modified_by_name: string | null;
}

interface BookingType {
  id: number;
  name: string;
}

interface BookingMode {
  id: number;
  name: string;
}

interface FormData {
  bookingMode: string;
  bookingType: string;
  packages: Array<{
    id: number;
    packageName: string;
    hours: string;
    kilometer: string;
  }>;
}

interface TableRow {
  sno: number;
  id: number;
  bookingMode: string;
  bookingType: string;
  packageName: string;
  hours: string;
  kilometer: string;
  status: string;
  originalData: LocalPackage;
}

const AddLocalPackageFare: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState<TableRow | null>(null);
  const [localPackages, setLocalPackages] = useState<LocalPackage[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [formData, setFormData] = useState<FormData>({
    bookingMode: "",
    bookingType: "",
    packages: [
      {
        id: 1,
        packageName: "",
        hours: "",
        kilometer: "",
      },
    ],
  });

  // API Hooks
  const getLocalPackages = useGetLocalPackages();
  const addLocalPackage = useAddLocalPackage();
  const updateLocalPackage = useUpdateLocalPackage();
  const updateStatus = useUpdateMasterLocalPackageStatus();
  const deleteLocalPackages = useDeleteLocalPackages();
  const bookingTypesAndModes = useGetBookingTypesAndModes();
  
  const user = useAuth();
  const loggedInUser = user?.user?.id;

  // Get booking types and modes from API
  const bookingTypes: BookingType[] = bookingTypesAndModes.data?.data?.booking_types || [];
  const bookingModes: BookingMode[] = bookingTypesAndModes.data?.data?.booking_modes || [];

  // Fetch local packages on component mount
  useEffect(() => {
    fetchLocalPackages();
  }, []);

  const fetchLocalPackages = () => {
    getLocalPackages.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setLocalPackages(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching local packages:", error);
        },
      }
    );
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentRow(null);
      setFormData({
        bookingMode: "",
        bookingType: "",
        packages: [
          {
            id: 1,
            packageName: "",
            hours: "",
            kilometer: "",
          },
        ],
      });
    }
  }, [isModalOpen]);

  // Set form data when currentRow changes (for editing)
  useEffect(() => {
    if (currentRow && isModalOpen) {
      // Find the actual IDs from the booking types and modes
      const bookingTypeId = bookingTypes.find(type => type.name === currentRow.bookingType)?.id?.toString() || "";
      const bookingModeId = bookingModes.find(mode => mode.name === currentRow.bookingMode)?.id?.toString() || "";

      setFormData({
        bookingMode: bookingModeId,
        bookingType: bookingTypeId,
        packages: [
          {
            id: 1,
            packageName: currentRow.packageName,
            hours: currentRow.hours,
            kilometer: currentRow.kilometer,
          },
        ],
      });
    }
  }, [currentRow, isModalOpen, bookingTypes, bookingModes]);

  const handleInputChange = (
    field: "bookingMode" | "bookingType",
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      // Convert string values to numbers, handle empty values
      const bookingModeValue = formData.bookingMode ? parseInt(formData.bookingMode) : undefined;
      const bookingTypeValue = formData.bookingType ? parseInt(formData.bookingType) : undefined;

      const payload = {
        name: formData.packageName,
        hrs: parseInt(formData.hours) || 0,
        km: parseInt(formData.kilometer) || 0,
        booking_mode: bookingModeValue,
        booking_type: bookingTypeValue,
        status: formData.status === "Active" ? 1 : 0,
        display_order: 1,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentRow) {
        // Update existing package - use auto_id instead of local_package_id
        const updatePayload = {
          ...payload,
          auto_id: currentRow.id, // Changed from local_package_id to auto_id
        };

        updateLocalPackage.mutate(
          updatePayload,
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchLocalPackages();
                setIsModalOpen(false);
                setCurrentRow(null);
              }
            },
            onError: (error) => {
              console.error("Update error:", error);
              toast.error("Failed to update package. Please try again.");
            },
          }
        );
      } else {
        // Add new package - don't include auto_id for creation
        addLocalPackage.mutate(
          payload,
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchLocalPackages();
                setIsModalOpen(false);
              }
            },
            onError: (error) => {
              console.error("Add error:", error);
              toast.error("Failed to add package. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleSaveAll = () => {
    if (!formData.bookingMode || !formData.bookingType) {
      toast.error("Please select booking mode and booking type first");
      return;
    }

    const validPackages = formData.packages.filter(
      pkg => pkg.packageName.trim() && pkg.hours && pkg.kilometer
    );

    if (validPackages.length === 0) {
      toast.error("Please add at least one valid package");
      return;
    }

    // Save all packages
    validPackages.forEach((pkg, index) => {
      const payload = {
        name: pkg.packageName,
        hrs: parseInt(pkg.hours) || 0,
        km: parseInt(pkg.kilometer) || 0,
        booking_mode: parseInt(formData.bookingMode),
        booking_type: parseInt(formData.bookingType),
        status: 1,
        display_order: index + 1,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      addLocalPackage.mutate(payload, {
        onSuccess: (data) => {
          if (data.status === "success") {
            if (index === validPackages.length - 1) {
              fetchLocalPackages();
              // Reset form after saving all
              setFormData({
                bookingMode: formData.bookingMode,
                bookingType: formData.bookingType,
                packages: [
                  {
                    id: 1,
                    packageName: "",
                    hours: "",
                    kilometer: "",
                  },
                ],
              });
            }
          }
        },
        onError: (error) => {
          toast.error(`Failed to save package "${pkg.packageName}". Please try again.`);
        },
      });
    });
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser,
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchLocalPackages();
            setSelectedRows([]);
          }
        },
        onError: (error) => {
          toast.error("Failed to publish packages. Please try again.");
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser,
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchLocalPackages();
            setSelectedRows([]);
          }
        },
        onError: (error) => {
          
          toast.error("Failed to unpublish packages. Please try again.");
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    const message = selectedRows.length === 1 
      ? "Are you sure you want to delete this package?"
      : `Are you sure you want to delete ${selectedRows.length} selected packages?`;

    if (confirm(message)) {
      deleteLocalPackages.mutate(
        {
          id: selectedRows.join(","),
          user_id: loggedInUser
        },
        {
          onSuccess: (data) => {
            if (data.status === "success") {
              fetchLocalPackages();
              setSelectedRows([]);
            }
          },
          onError: (error) => {
            toast.error("Failed to delete package(s). Please try again.");
            
          }
        }
      );
    }
  };

  const handlePackageChange = (
    packageId: number,
    field: "packageName" | "hours" | "kilometer",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      packages: prev.packages.map((pkg) =>
        pkg.id === packageId ? { ...pkg, [field]: value } : pkg
      ),
    }));
  };

  const addMorePackage = () => {
    const newId = Math.max(...formData.packages.map((p) => p.id)) + 1;
    setFormData((prev) => ({
      ...prev,
      packages: [
        ...prev.packages,
        {
          id: newId,
          packageName: "",
          hours: "",
          kilometer: "",
        },
      ],
    }));
  };

  const removePackage = (packageId: number) => {
    if (formData.packages.length > 1) {
      setFormData((prev) => ({
        ...prev,
        packages: prev.packages.filter((pkg) => pkg.id !== packageId),
      }));
    }
  };

  // Transform API data to match table structure
  const tableData: TableRow[] = localPackages.map((item, index) => ({
    sno: index + 1,
    id: item.local_package_id,
    bookingMode: item.booking_mode_name,
    bookingType: item.booking_type_name,
    packageName: item.package_name,
    hours: item.hrs.toString(),
    kilometer: item.km.toString(),
    status: item.status === 1 ? "Active" : "Inactive",
    originalData: item,
  }));

  const columns: any = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "bookingMode", header: "Booking Mode", sortable: true },
    { key: "bookingType", header: "Booking Type", sortable: true },
    { key: "packageName", header: "Package Name", sortable: true },
    { key: "hours", header: "Hours", sortable: true },
    { key: "kilometer", header: "Kilometer", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value === "Active"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: TableRow) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentRow(row);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (confirm("Are you sure you want to delete this package?")) {
                  // Delete single package directly
                  deleteLocalPackages.mutate(
                    {
                      id: row.id.toString(),
                      user_id: loggedInUser
                    },
                    {
                      onSuccess: (data) => {
                        if (data.status === "success") {
                          fetchLocalPackages();
                        }
                      },
                      onError: (error) => {
                        toast.error("Failed to delete package. Please try again.");
                      }
                    }
                  );
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  const formFields: any = [
    {
      id: "bookingMode",
      label: "Booking Mode *",
      type: "select",
      placeholder: "Select Booking Mode",
      required: true,
      defaultValue: currentRow ? formData.bookingMode : "",
      onChange: (value: string) => handleInputChange("bookingMode", value),
      options: bookingModes.map(mode => ({
        value: mode.id.toString(),
        label: mode.name
      })),
    },
    {
      id: "bookingType",
      label: "Booking Type *",
      type: "select",
      placeholder: "Select Booking Type",
      defaultValue: currentRow ? formData.bookingType : "",
      onChange: (value: string) => handleInputChange("bookingType", value),
      required: true,
      options: bookingTypes.map(type => ({
        value: type.id.toString(),
        label: type.name
      })),
    },
    {
      id: "packageName",
      label: "Package Name *",
      type: "text",
      required: true,
      defaultValue: currentRow?.packageName || formData.packages[0].packageName,
      placeholder: "Enter Package Name",
    },
    {
      id: "hours",
      label: "Hours *",
      type: "number",
      required: true,
      defaultValue: currentRow?.hours || formData.packages[0].hours,
      placeholder: "Enter Hours",
    },
    {
      id: "kilometer",
      label: "Kilometer *",
      type: "number",
      required: true,
      defaultValue: currentRow?.kilometer || formData.packages[0].kilometer,
      placeholder: "Enter Kilometer",
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        {/* Top Row - Dropdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Booking Mode */}
          <div>
            <label className="block text-sm text-gray-700 mb-2">
              Booking Mode <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={formData.bookingMode}
                onChange={(e) =>
                  handleInputChange("bookingMode", e.target.value)
                }
                className="w-full px-3 py-1 border border-gray-300 rounded text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                disabled={bookingTypesAndModes.isLoading}
              >
                <option value="">--Select Mode--</option>
                {bookingModes.map((mode) => (
                  <option key={mode.id} value={mode.id.toString()}>
                    {mode.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              {bookingTypesAndModes.isLoading && (
                <div className="absolute right-8 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Type */}
          <div>
            <label className="block text-sm text-gray-700 mb-2">
              Booking Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={formData.bookingType}
                onChange={(e) =>
                  handleInputChange("bookingType", e.target.value)
                }
                className="w-full px-3 py-1 border border-gray-300 rounded text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                disabled={bookingTypesAndModes.isLoading}
              >
                <option value="">--Select Booking Type--</option>
                {bookingTypes.map((type) => (
                  <option key={type.id} value={type.id.toString()}>
                    {type.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              {bookingTypesAndModes.isLoading && (
                <div className="absolute right-8 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Dynamic Package Rows */}
        <div className="space-y-4">
          {formData.packages.map((pkg, index) => (
            <div
              key={pkg.id}
              className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end"
            >
              {/* Package Name */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Package Name
                </label>
                <input
                  type="text"
                  value={pkg.packageName}
                  onChange={(e) =>
                    handlePackageChange(pkg.id, "packageName", e.target.value)
                  }
                  className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter package name"
                />
              </div>

              {/* Hours */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Hours
                </label>
                <input
                  type="number"
                  value={pkg.hours}
                  onChange={(e) =>
                    handlePackageChange(pkg.id, "hours", e.target.value)
                  }
                  className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter hours"
                />
              </div>

              {/* Kilometer */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Kilometer
                </label>
                <input
                  type="number"
                  value={pkg.kilometer}
                  onChange={(e) =>
                    handlePackageChange(pkg.id, "kilometer", e.target.value)
                  }
                  className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter kilometers"
                />
              </div>

              {/* Add More / Remove Button */}
              <div>
                {index === formData.packages.length - 1 ? (
                  <Button
                    size="sm"
                    onClick={addMorePackage}
                    icon={<Plus className="w-4 h-4" />}
                    iconPosition="left"
                  >
                    Add
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => removePackage(pkg.id)}
                    icon={<X className="w-4 h-4" />}
                    iconPosition="left"
                  >
                    Remove
                  </Button>
                )}
              </div>

              {/* Save Button - only show on first row */}
              {index === 0 && (
                <div>
                  <Button
                    size="sm"
                    onClick={handleSaveAll}
                    icon={<Save className="w-4 h-4" />}
                    iconPosition="left"
                    disabled={addLocalPackage.isPending || !formData.bookingMode || !formData.bookingType}
                  >
                    {addLocalPackage.isPending ? "Saving..." : "Save All"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || deleteLocalPackages.isPending}
          >
            {deleteLocalPackages.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={false}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={getLocalPackages.isPending || bookingTypesAndModes.isLoading}
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Mode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kilometer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentRow(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentRow ? "Update Package" : "Add Package"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentRow(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={handleFormSubmit}
                  submitText={currentRow ? "Update Package" : "Add Package"}
                  loading={addLocalPackage.isPending || updateLocalPackage.isPending || bookingTypesAndModes.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddLocalPackageFare;

// "use client";

// import React, { useEffect, useState } from "react";
// import {
//   ChevronDown,
//   Plus,
//   X,
//   Trash2,
//   Edit,
//   toast.errorTriangleIcon,
//   DownloadCloudIcon,
//   TrashIcon,
//   UploadIcon,
//   Save,
// } from "lucide-react";
// import BookingTable from "../common/Table";
// import DynamicForm from "./Popup";
// import Button from "./Button";
// import { 
//   useGetLocalPackages, 
//   useAddLocalPackage, 
//   useUpdateLocalPackage, 
//   useUpdateMasterLocalPackageStatus, 
//   useDeleteLocalPackages,
//   useGetBookingTypesAndModes 
// } from "@/hooks/useCommon";
// import { useAuth } from "@/store/auth";

// interface LocalPackage {
//   local_package_id: number;
//   booking_type: number;
//   booking_mode: number;
//   booking_mode_name: string;
//   booking_type_name: string;
//   package_name: string;
//   hrs: number;
//   km: number;
//   status: number;
//   display_order: number;
//   modified_by_name: string | null;
// }

// interface BookingType {
//   id: number;
//   name: string;
// }

// interface BookingMode {
//   id: number;
//   name: string;
// }

// interface FormData {
//   bookingMode: string;
//   bookingType: string;
//   packages: Array<{
//     id: number;
//     packageName: string;
//     hours: string;
//     kilometer: string;
//   }>;
// }

// interface TableRow {
//   sno: number;
//   id: number;
//   bookingMode: string;
//   bookingType: string;
//   packageName: string;
//   hours: string;
//   kilometer: string;
//   status: string;
//   originalData: LocalPackage;
// }

// const AddLocalPackageFare: React.FC = () => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [currentRow, setCurrentRow] = useState<TableRow | null>(null);
//   const [localPackages, setLocalPackages] = useState<LocalPackage[]>([]);
//   const [selectedRows, setSelectedRows] = useState<number[]>([]);
//   const [formData, setFormData] = useState<FormData>({
//     bookingMode: "",
//     bookingType: "",
//     packages: [
//       {
//         id: 1,
//         packageName: "",
//         hours: "",
//         kilometer: "",
//       },
//     ],
//   });

//   // API Hooks
//   const getLocalPackages = useGetLocalPackages();
//   const addLocalPackage = useAddLocalPackage();
//   const updateLocalPackage = useUpdateLocalPackage();
//   const updateStatus = useUpdateMasterLocalPackageStatus();
//   const deleteLocalPackages = useDeleteLocalPackages();
//   const bookingTypesAndModes = useGetBookingTypesAndModes();
  
//   const user = useAuth();
//   const loggedInUser = user?.user?.id;

//   // Get booking types and modes from API
//   const bookingTypes: BookingType[] = bookingTypesAndModes.data?.data?.booking_types || [];
//   const bookingModes: BookingMode[] = bookingTypesAndModes.data?.data?.booking_modes || [];

//   // Fetch local packages on component mount
//   useEffect(() => {
//     fetchLocalPackages();
//   }, []);

//   const fetchLocalPackages = () => {
//     getLocalPackages.mutate(
//       {},
//       {
//         onSuccess: (data) => {
//           if (data.status === "success") {
//             setLocalPackages(data.data);
//           }
//         },
//         onError: (error) => {
//           console.error("Error fetching local packages:", error);
//         },
//       }
//     );
//   };

//   // Reset states when modal opens/closes
//   useEffect(() => {
//     if (!isModalOpen) {
//       setCurrentRow(null);
//       setFormData({
//         bookingMode: "",
//         bookingType: "",
//         packages: [
//           {
//             id: 1,
//             packageName: "",
//             hours: "",
//             kilometer: "",
//           },
//         ],
//       });
//     }
//   }, [isModalOpen]);

//   // Set form data when currentRow changes (for editing)
//   useEffect(() => {
//     if (currentRow && isModalOpen) {
//       // Find the actual IDs from the booking types and modes
//       const bookingTypeId = bookingTypes.find(type => type.name === currentRow.bookingType)?.id?.toString() || "";
//       const bookingModeId = bookingModes.find(mode => mode.name === currentRow.bookingMode)?.id?.toString() || "";

//       setFormData({
//         bookingMode: bookingModeId,
//         bookingType: bookingTypeId,
//         packages: [
//           {
//             id: 1,
//             packageName: currentRow.packageName,
//             hours: currentRow.hours,
//             kilometer: currentRow.kilometer,
//           },
//         ],
//       });
//     }
//   }, [currentRow, isModalOpen, bookingTypes, bookingModes]);

//   const handleInputChange = (
//     field: "bookingMode" | "bookingType",
//     value: string
//   ) => {
//     setFormData((prev) => ({ ...prev, [field]: value }));
//   };

//   const handleFormSubmit = async (formData: Record<string, any>) => {
//     try {
//       const payload = {
//         name: formData.packageName,
//         hrs: parseInt(formData.hours) || 0,
//         km: parseInt(formData.kilometer) || 0,
//         booking_mode: parseInt(formData.bookingMode),
//         booking_type: parseInt(formData.bookingType),
//         status: formData.status === "Active" ? 1 : 0,
//         display_order: 1,
//         ip: "127.0.0.1",
//         created_by: loggedInUser,
//         modified_by: loggedInUser,
//       };
//       console.log("Payload:", payload);

//       if (currentRow) {
//         // Update existing package
//         updateLocalPackage.mutate(
//           {
//             ...payload,
//             auto_id: currentRow.id,
//           },
//           {
//             onSuccess: (data) => {
//               if (data.status === "success") {
//                 fetchLocalPackages();
//                 setIsModalOpen(false);
//                 setCurrentRow(null);
//               }
//             },
//             onError: (error) => {
//               toast.error("Failed to update package. Please try again.");
//             },
//           }
//         );
//       } else {
//         // Add new package
//         addLocalPackage.mutate(
//           payload,
//           {
//             onSuccess: (data) => {
//               if (data.status === "success") {
//                 fetchLocalPackages();
//                 setIsModalOpen(false);
//               }
//             },
//             onError: (error) => {
//               toast.error("Failed to add package. Please try again.");
//             },
//           }
//         );
//       }
//     } catch (error) {
//       toast.error("An error occurred. Please try again.");
//     }
//   };

//   const handleSaveAll = () => {
//     if (!formData.bookingMode || !formData.bookingType) {
//       toast.error("Please select booking mode and booking type first");
//       return;
//     }

//     const validPackages = formData.packages.filter(
//       pkg => pkg.packageName.trim() && pkg.hours && pkg.kilometer
//     );

//     if (validPackages.length === 0) {
//       toast.error("Please add at least one valid package");
//       return;
//     }

//     // Save all packages
//     validPackages.forEach((pkg, index) => {
//       const payload = {
//         name: pkg.packageName,
//         hrs: parseInt(pkg.hours) || 0,
//         km: parseInt(pkg.kilometer) || 0,
//         booking_mode: parseInt(formData.bookingMode),
//         booking_type: parseInt(formData.bookingType),
//         status: 1,
//         display_order: index + 1,
//         ip: "127.0.0.1",
//         created_by: loggedInUser,
//         modified_by: loggedInUser,
//       };

//       addLocalPackage.mutate(payload, {
//         onSuccess: (data) => {
//           if (data.status === "success") {
//             if (index === validPackages.length - 1) {
//               fetchLocalPackages();
//               // Reset form after saving all
//               setFormData({
//                 bookingMode: formData.bookingMode,
//                 bookingType: formData.bookingType,
//                 packages: [
//                   {
//                     id: 1,
//                     packageName: "",
//                     hours: "",
//                     kilometer: "",
//                   },
//                 ],
//               });
//             }
//           }
//         },
//         onError: (error) => {
//           toast.error(`Failed to save package "${pkg.packageName}". Please try again.`);
//         },
//       });
//     });
//   };

//   const handlePublish = () => {
//     if (selectedRows.length === 0) return;

//     updateStatus.mutate(
//       {
//         id: selectedRows.join(","),
//         user_id: loggedInUser,
//         status: 1,
//       },
//       {
//         onSuccess: (data) => {
//           if (data.status === "success") {
//             fetchLocalPackages();
//             setSelectedRows([]);
//           }
//         },
//         onError: (error) => {
//           toast.error("Failed to publish packages. Please try again.");
//         },
//       }
//     );
//   };

//   const handleUnpublish = () => {
//     if (selectedRows.length === 0) return;

//     updateStatus.mutate(
//       {
//         id: selectedRows.join(","),
//         user_id: loggedInUser,
//         status: 0,
//       },
//       {
//         onSuccess: (data) => {
//           if (data.status === "success") {
//             fetchLocalPackages();
//             setSelectedRows([]);
//           }
//         },
//         onError: (error) => {
//           toast.error("Failed to unpublish packages. Please try again.");
//         },
//       }
//     );
//   };

//   const handleDelete = () => {
//     if (selectedRows.length === 0) return;

//     const message = selectedRows.length === 1 
//       ? "Are you sure you want to delete this package?"
//       : `Are you sure you want to delete ${selectedRows.length} selected packages?`;

//     if (confirm(message)) {
//       deleteLocalPackages.mutate(
//         {
//           id: selectedRows.join(","),
//           user_id: loggedInUser
//         },
//         {
//           onSuccess: (data) => {
//             if (data.status === "success") {
//               fetchLocalPackages();
//               setSelectedRows([]);
//             }
//           },
//           onError: (error) => {
//             toast.error("Failed to delete package(s). Please try again.");
//           }
//         }
//       );
//     }
//   };

//   const handlePackageChange = (
//     packageId: number,
//     field: "packageName" | "hours" | "kilometer",
//     value: string
//   ) => {
//     setFormData((prev) => ({
//       ...prev,
//       packages: prev.packages.map((pkg) =>
//         pkg.id === packageId ? { ...pkg, [field]: value } : pkg
//       ),
//     }));
//   };

//   const addMorePackage = () => {
//     const newId = Math.max(...formData.packages.map((p) => p.id)) + 1;
//     setFormData((prev) => ({
//       ...prev,
//       packages: [
//         ...prev.packages,
//         {
//           id: newId,
//           packageName: "",
//           hours: "",
//           kilometer: "",
//         },
//       ],
//     }));
//   };

//   const removePackage = (packageId: number) => {
//     if (formData.packages.length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         packages: prev.packages.filter((pkg) => pkg.id !== packageId),
//       }));
//     }
//   };

//   // Transform API data to match table structure
//   const tableData: TableRow[] = localPackages.map((item, index) => ({
//     sno: index + 1,
//     id: item.local_package_id,
//     bookingMode: item.booking_mode_name,
//     bookingType: item.booking_type_name,
//     packageName: item.package_name,
//     hours: item.hrs.toString(),
//     kilometer: item.km.toString(),
//     status: item.status === 1 ? "Active" : "Inactive",
//     originalData: item,
//   }));

//   const columns: any = [
//     {
//       key: "selection",
//       header: "",
//       sortable: false,
//       render: (_: any, row: any) => (
//         <input
//           type="checkbox"
//           checked={selectedRows.includes(row.id)}
//           onChange={(e) => {
//             if (e.target.checked) {
//               setSelectedRows([...selectedRows, row.id]);
//             } else {
//               setSelectedRows(selectedRows.filter((id) => id !== row.id));
//             }
//           }}
//           className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
//         />
//       ),
//     },
//     { key: "sno", header: "S.No", sortable: true },
//     { key: "bookingMode", header: "Booking Mode", sortable: true },
//     { key: "bookingType", header: "Booking Type", sortable: true },
//     { key: "packageName", header: "Package Name", sortable: true },
//     { key: "hours", header: "Hours", sortable: true },
//     { key: "kilometer", header: "Kilometer", sortable: true },
//     {
//       key: "status",
//       header: "Status",
//       sortable: true,
//       render: (value: string) => (
//         <span
//           className={`px-2 py-1 rounded-full text-xs font-medium ${
//             value === "Active"
//               ? "bg-green-100 text-green-800"
//               : "bg-red-100 text-red-800"
//           }`}
//         >
//           {value}
//         </span>
//       ),
//     },
//     {
//       key: "action",
//       header: "Action",
//       render: (_: any, row: TableRow) => {
//         return (
//           <div className="flex space-x-2">
//             <button
//               className="text-blue-500 hover:text-blue-700"
//               onClick={() => {
//                 setCurrentRow(row);
//                 setIsModalOpen(true);
//               }}
//             >
//               <Edit size={15} />
//             </button>
//             <button
//               className="text-red-500 hover:text-red-700"
//               onClick={() => {
//                 if (confirm("Are you sure you want to delete this package?")) {
//                   // Delete single package directly
//                   deleteLocalPackages.mutate(
//                     {
//                       id: row.id.toString(),
//                       user_id: loggedInUser
//                     },
//                     {
//                       onError: (error) => {
//                         toast.error("Failed to delete package. Please try again.");
//                       }
//                     }
//                   );
//                 }
//               }}
//             >
//               <Trash2 size={15} />
//             </button>
//           </div>
//         );
//       },
//     },
//   ];

//   const formFields: any = [
//     {
//       id: "bookingMode",
//       label: "Booking Mode *",
//       type: "select",
//       placeholder: "Select Booking Mode",
//       required: true,
//       defaultValue: currentRow ? formData.bookingMode : "",
//       onChange: (value: string) => handleInputChange("bookingMode", value),
//       options: bookingModes.map(mode => ({
//         value: mode.id.toString(),
//         label: mode.name
//       })),
//     },
//     {
//       id: "bookingType",
//       label: "Booking Type *",
//       type: "select",
//       placeholder: "Select Booking Type",
//       defaultValue: currentRow ? formData.bookingType : "",
//       onChange: (value: string) => handleInputChange("bookingType", value),
//       required: true,
//       options: bookingTypes.map(type => ({
//         value: type.id.toString(),
//         label: type.name
//       })),
//     },
//     {
//       id: "packageName",
//       label: "Package Name *",
//       type: "text",
//       required: true,
//       defaultValue: currentRow?.packageName || formData.packages[0].packageName,
//       placeholder: "Enter Package Name",
//     },
//     {
//       id: "hours",
//       label: "Hours *",
//       type: "number",
//       required: true,
//       defaultValue: currentRow?.hours || formData.packages[0].hours,
//       placeholder: "Enter Hours",
//     },
//     {
//       id: "kilometer",
//       label: "Kilometer *",
//       type: "number",
//       required: true,
//       defaultValue: currentRow?.kilometer || formData.packages[0].kilometer,
//       placeholder: "Enter Kilometer",
//     },
   
//   ];

//   // Select all checkbox handler
//   const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
//     if (e.target.checked) {
//       setSelectedRows(tableData.map((row) => row.id));
//     } else {
//       setSelectedRows([]);
//     }
//   };

//   return (
//     <>
//       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
//         {/* Top Row - Dropdowns */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
//           {/* Booking Mode */}
//           <div>
//             <label className="block text-sm text-gray-700 mb-2">
//               Booking Mode <span className="text-red-500">*</span>
//             </label>
//             <div className="relative">
//               <select
//                 value={formData.bookingMode}
//                 onChange={(e) =>
//                   handleInputChange("bookingMode", e.target.value)
//                 }
//                 className="w-full px-3 py-1 border border-gray-300 rounded text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
//                 disabled={bookingTypesAndModes.isLoading}
//               >
//                 <option value="">--Select Mode--</option>
//                 {bookingModes.map((mode) => (
//                   <option key={mode.id} value={mode.id.toString()}>
//                     {mode.name}
//                   </option>
//                 ))}
//               </select>
//               <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
//               {bookingTypesAndModes.isLoading && (
//                 <div className="absolute right-8 top-2.5">
//                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Booking Type */}
//           <div>
//             <label className="block text-sm text-gray-700 mb-2">
//               Booking Type <span className="text-red-500">*</span>
//             </label>
//             <div className="relative">
//               <select
//                 value={formData.bookingType}
//                 onChange={(e) =>
//                   handleInputChange("bookingType", e.target.value)
//                 }
//                 className="w-full px-3 py-1 border border-gray-300 rounded text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
//                 disabled={bookingTypesAndModes.isLoading}
//               >
//                 <option value="">--Select Booking Type--</option>
//                 {bookingTypes.map((type) => (
//                   <option key={type.id} value={type.id.toString()}>
//                     {type.name}
//                   </option>
//                 ))}
//               </select>
//               <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
//               {bookingTypesAndModes.isLoading && (
//                 <div className="absolute right-8 top-2.5">
//                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Bottom Section - Dynamic Package Rows */}
//         <div className="space-y-4">
//           {formData.packages.map((pkg, index) => (
//             <div
//               key={pkg.id}
//               className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end"
//             >
//               {/* Package Name */}
//               <div>
//                 <label className="block text-sm text-gray-700 mb-2">
//                   Package Name
//                 </label>
//                 <input
//                   type="text"
//                   value={pkg.packageName}
//                   onChange={(e) =>
//                     handlePackageChange(pkg.id, "packageName", e.target.value)
//                   }
//                   className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter package name"
//                 />
//               </div>

//               {/* Hours */}
//               <div>
//                 <label className="block text-sm text-gray-700 mb-2">
//                   Hours
//                 </label>
//                 <input
//                   type="number"
//                   value={pkg.hours}
//                   onChange={(e) =>
//                     handlePackageChange(pkg.id, "hours", e.target.value)
//                   }
//                   className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter hours"
//                 />
//               </div>

//               {/* Kilometer */}
//               <div>
//                 <label className="block text-sm text-gray-700 mb-2">
//                   Kilometer
//                 </label>
//                 <input
//                   type="number"
//                   value={pkg.kilometer}
//                   onChange={(e) =>
//                     handlePackageChange(pkg.id, "kilometer", e.target.value)
//                   }
//                   className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter kilometers"
//                 />
//               </div>

//               {/* Add More / Remove Button */}
//               <div>
//                 {index === formData.packages.length - 1 ? (
//                   <Button
//                     size="sm"
//                     onClick={addMorePackage}
//                     icon={<Plus className="w-4 h-4" />}
//                     iconPosition="left"
//                   >
//                     Add
//                   </Button>
//                 ) : (
//                   <Button
//                     size="sm"
//                     onClick={() => removePackage(pkg.id)}
//                     icon={<X className="w-4 h-4" />}
//                     iconPosition="left"
//                   >
//                     Remove
//                   </Button>
//                 )}
//               </div>

//               {/* Save Button - only show on first row */}
//               {index === 0 && (
//                 <div>
//                   <Button
//                     size="sm"
//                     onClick={handleSaveAll}
//                     icon={<Save className="w-4 h-4" />}
//                     iconPosition="left"
//                     disabled={addLocalPackage.isPending || !formData.bookingMode || !formData.bookingType}
//                   >
//                     {addLocalPackage.isPending ? "Saving..." : "Save All"}
//                   </Button>
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       <div className="mt-10">
//         <div className="flex justify-end gap-2">
//           <Button
//             size="sm"
//             onClick={handlePublish}
//             icon={<UploadIcon className="w-4 h-4" />}
//             iconPosition="left"
//             disabled={selectedRows.length === 0 || updateStatus.isPending}
//           >
//             {updateStatus.isPending ? "Publishing..." : "Publish"}
//           </Button>

//           <Button
//             size="sm"
//             onClick={handleUnpublish}
//             icon={<DownloadCloudIcon className="w-4 h-4" />}
//             iconPosition="left"
//             disabled={selectedRows.length === 0 || updateStatus.isPending}
//           >
//             {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
//           </Button>

//           <Button
//             size="sm"
//             variant="danger"
//             onClick={handleDelete}
//             icon={<TrashIcon className="w-4 h-4" />}
//             iconPosition="left"
//             disabled={selectedRows.length === 0 || deleteLocalPackages.isPending}
//           >
//             {deleteLocalPackages.isPending ? "Deleting..." : "Delete"}
//           </Button>
//         </div>

//         <BookingTable
//           data={tableData}
//           columns={columns}
//           heading={[]}
//           searchable={true}
//           filterable={false}
//           sortable={true}
//           selectable={true}
//           onSelectionChange={setSelectedRows}
//           loading={getLocalPackages.isPending || bookingTypesAndModes.isLoading}
//           renderHeader={() => (
//             <thead className="bg-gray-50">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   <input
//                     type="checkbox"
//                     checked={
//                       selectedRows.length === tableData.length &&
//                       tableData.length > 0
//                     }
//                     onChange={handleSelectAll}
//                     className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
//                   />
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   S.No
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Booking Mode
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Booking Type
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Package Name
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Hours
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Kilometer
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Status
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Action
//                 </th>
//               </tr>
//             </thead>
//           )}
//         />
//       </div>

//       {isModalOpen && (
//         <div className="fixed inset-0 z-50 overflow-y-auto">
//           <div
//             className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
//             onClick={() => {
//               setIsModalOpen(false);
//               setCurrentRow(null);
//             }}
//           />

//           <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
//             <div
//               className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
//                 <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
//                   {currentRow ? "Update Package" : "Add Package"}
//                 </h2>

//                 <button
//                   onClick={() => {
//                     setIsModalOpen(false);
//                     setCurrentRow(null);
//                   }}
//                   className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
//                   aria-label="Close modal"
//                 >
//                   <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
//                 </button>
//               </div>

//               <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
//                 <DynamicForm
//                   fields={formFields}
//                   onSubmit={handleFormSubmit}
//                   submitText={currentRow ? "Update Package" : "Add Package"}
//                   loading={addLocalPackage.isPending || updateLocalPackage.isPending || bookingTypesAndModes.isLoading}
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default AddLocalPackageFare;


